import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';

import {
  JOB_STATUS_COMPLETED,
} from 'common/jobConstants';

import {
  isListingSunRise,
  shouldHideSunlockCodes,
} from 'common/listingHelpers';

import { MEDIA_QUERY_SMALL_SCREEN } from 'client/constants/mediaQueries';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrInstructionsTile from 'client/components/SrInstructionsTile/SrInstructionsTile';
import SrPartnerSunlockInstructionsModal from 'client/components/SrPartnerSunlockInstructionsModal/SrPartnerSunlockInstructionsModal';
import SrPartnerComboLockboxInstructionsModal from 'client/components/SrPartnerComboLockboxInstructionsModal/SrPartnerComboLockboxInstructionsModal';
import SrPartnerSupraLockboxInstructionsModal from 'client/components/SrPartnerSupraLockboxInstructionsModal/SrPartnerSupraLockboxInstructionsModal';

const SrPartnerListingAccessInstructions = ({
  listing,
  currentUser,
  marketingPartnerListing,
}) => {
  const isSmallScreen = useMediaQuery({
    query: MEDIA_QUERY_SMALL_SCREEN,
  });

  const [showSunlockInstructionsModal, setShowSunlockInstructionsModal] = useState(false);
  const [showComboLockboxInstructionsModal, setShowComboLockboxInstructionsModal] = useState(false);
  const [showSupraLockboxInstructionsModal, setShowSupraLockboxInstructionsModal] = useState(false);
  const isSunRiseListing = isListingSunRise(listing);
  const shouldShowSupraSection = !isSunRiseListing
    && marketingPartnerListing?.supraLockInstallJob?.status === JOB_STATUS_COMPLETED;
  const shouldShowComboLockbox = isSunRiseListing
    ? listing.lockboxCode != null
    : (
      marketingPartnerListing?.lockboxCode
      || marketingPartnerListing?.comboLockInstallJob?.status === JOB_STATUS_COMPLETED
    );
  const shouldShowSelfShowingHardware = isSunRiseListing
    && marketingPartnerListing?.requireSelfShowingHardware
    && marketingPartnerListing?.selfShowingHardwareInstallJob?.status === JOB_STATUS_COMPLETED;
  const selfShowingHardwareInstructions = marketingPartnerListing
    ?.marketingItemsConfiguration?.marketingItemsConfiguration?.selfShowingHardwareNotes
    || 'Go to TenantTurner to get the access code.';

  const comboLockboxOnClick = !isSunRiseListing
    ? () => setShowComboLockboxInstructionsModal(true)
    : undefined;
  const getComboLockboxInstructions = () => {
    if (isSunRiseListing) {
      return `Inside are the keys/items ${marketingPartnerListing?.partner?.displayName} setup.`;
    }

    if (marketingPartnerListing?.sunlock) {
      return <SrSunlockInstructions />;
    }

    return 'Inside is the owner\'s key to the original lock.';
  };

  if (isSunRiseListing && !shouldShowComboLockbox && !shouldShowSelfShowingHardware) {
    return <SrNoItemsFound isSmallScreen={isSmallScreen} />;
  }

  return (
    <>
      <SrBox
        backgroundColor='white'
        borderRadius={isSmallScreen ? '14px' : '24px'}
        boxShadow='0px 5px 21px rgba(25, 51, 64, 0.05)'
      >
        {marketingPartnerListing?.sunlock && (
          <SrInstructionsTile
            title='Sunlock Installed'
            instructions={'Enter this code into the Sunlock keypad on the door. ' +
              'Remember, your original key will not work.'}
            subtitle={shouldHideSunlockCodes(listing, currentUser) ? null : (
              <SrBox display='inlineBlock'>
                <SrText
                  letterSpacing='0px'
                  font='greycliff'
                  weight='600'
                  size='16'
                  color='blueGreycliff'
                  lineHeight='21px'
                >
                  Owner's Access Code:&thinsp;
                  <SrText
                    letterSpacing='0px'
                    font='greycliff'
                    weight='800'
                    size='18'
                    color='blueGreycliff'
                    lineHeight='21px'
                  >
                    {marketingPartnerListing.sunlock.partnerLockCode}
                  </SrText>
                </SrText>
              </SrBox>
            )}
            color='#FFF4CB'
            iconUrl='/images/keypad-icon.svg'
            iconDropShadowColor='rgba(168, 135, 0, 0.18)'
            onClick={() => setShowSunlockInstructionsModal(true)}
          />
        )}
        {marketingPartnerListing?.codeboxLock && (
          <SrInstructionsTile
            title='Codebox Installed'
            instructions={shouldHideSunlockCodes(listing, currentUser)
              ? null
              : 'Enter this code into the Codebox keypad on the door.'
            }
            subtitle={shouldHideSunlockCodes(listing, currentUser) ? null : (
              <SrBox display='inlineBlock'>
                <SrText
                  letterSpacing='0px'
                  font='greycliff'
                  weight='600'
                  size='16'
                  color='blueGreycliff'
                  lineHeight='21px'
                >
                  Master Code:&nbsp;
                  <SrText
                    letterSpacing='0px'
                    font='greycliff'
                    weight='800'
                    size='18'
                    color='blueGreycliff'
                    lineHeight='21px'
                  >
                    {marketingPartnerListing.codeboxLock.masterCode}
                  </SrText>
                </SrText>
              </SrBox>
            )}
            color='#FFF4CB'
            iconUrl='/images/keypad-icon.svg'
            iconDropShadowColor='rgba(168, 135, 0, 0.18)'
          />
        )}
        {shouldShowComboLockbox && (
          <SrInstructionsTile
            title='Combo Lockbox Installed'
            instructions={getComboLockboxInstructions()}
            subtitle={shouldHideSunlockCodes(listing, currentUser) ? null : (
              <>
                <SrText
                  letterSpacing='0px'
                  font='greycliff'
                  weight='700'
                  size='16'
                  color='blueGreycliff'
                  lineHeight='21px'
                >
                  Code:&nbsp;
                </SrText>
                <SrText
                  letterSpacing='0px'
                  font='greycliff'
                  weight='800'
                  size='18'
                  color='blueGreycliff'
                  lineHeight='21px'
                >
                  {listing.lockboxCode}
                </SrText>
              </>
            )}
            iconUrl='/images/combo-lock-icon.svg'
            iconDropShadowColor='rgba(19, 38, 48, 0.16)'
            iconSize='21px'
            onClick={comboLockboxOnClick}
          />
        )}
        {shouldShowSelfShowingHardware && (
          <SrInstructionsTile
            title='Self-Showing Hardware'
            subtitle={(
              <>
                <SrText
                  letterSpacing='0px'
                  font='greycliff'
                  weight='700'
                  size='16'
                  color='blueGreycliff'
                  lineHeight='21px'
                >
                  Serial #:&nbsp;
                </SrText>
                <SrText
                  letterSpacing='0px'
                  font='greycliff'
                  weight='800'
                  size='18'
                  color='blueGreycliff'
                  lineHeight='21px'
                >
                  {listing.selfShowingHardwareSerialNumber}
                </SrText>
              </>
            )}
            iconUrl='/images/self-showing-hardware-icon.svg'
            iconSize='51px'
            iconDropShadowColor='rgba(19, 38, 48, 0.16)'
            instructions={selfShowingHardwareInstructions}
            iconStyles={{ paddingTop: 14 }}
          />
        )}
        {shouldShowSupraSection && (
          <SrInstructionsTile
            title='Supra Installed'
            iconUrl='/images/supra-lock-icon.svg'
            iconSize='16px'
            color='rgba(77, 106, 133, 0.15)'
            iconDropShadowColor='rgba(58, 80, 100, 0.18)'
            instructions={marketingPartnerListing?.sunlock
              ? 'Inside is one physical key to open the Sunlock.'
              : 'Inside is the owner\'s key to the front door.'
            }
            onClick={() => setShowSupraLockboxInstructionsModal(true)}
          />
        )}
        {showSunlockInstructionsModal && (
          <SrPartnerSunlockInstructionsModal
            onClose={() => setShowSunlockInstructionsModal(false)}
            sunlockPhotoUrl={
              marketingPartnerListing?.sunlockPhotoUrls && marketingPartnerListing?.sunlockPhotoUrls[0]
            }
            sunlockCode={marketingPartnerListing?.sunlock?.partnerLockCode}
            sunlockGuestCode={marketingPartnerListing?.sunlock?.lockCode}
            hideSunlockCodes={shouldHideSunlockCodes(listing, currentUser)}
          />
        )}
        {showComboLockboxInstructionsModal && (
          <SrPartnerComboLockboxInstructionsModal
            onClose={() => setShowComboLockboxInstructionsModal(false)}
            comboLockboxPhotoUrl={listing.comboPhotoUrls && listing.comboPhotoUrls[0]}
            comboLockboxCode={listing.lockboxCode}
            sunlockInstalled={marketingPartnerListing?.sunlock}
          />
        )}
        {showSupraLockboxInstructionsModal && (
          <SrPartnerSupraLockboxInstructionsModal
            onClose={() => setShowSupraLockboxInstructionsModal(false)}
            supraLockboxPhotoUrl={
              marketingPartnerListing.lockboxPhotoUrls && marketingPartnerListing.lockboxPhotoUrls[0]
            }
            sunlockInstalled={marketingPartnerListing?.sunlock}
          />
        )}
      </SrBox>
    </>
  );
};

const SrNoItemsFound = ({ isSmallScreen }) => (
  <SrBox
    backgroundColor='white'
    padding='20px'
    borderRadius={isSmallScreen ? '14px' : '24px'}
    boxShadow='0px 5px 21px rgba(25, 51, 64, 0.05)'
  >
    <SrFlex align='center' justify='center'>
      <SrText
        font='greycliff'
        weight='800'
        size='18'
        color='blueGreycliff'
        lineHeight='22px'
      >
        No items found
      </SrText>
    </SrFlex>
  </SrBox>
);

const SrSunlockInstructions = () => (
  <>
    <SrText
      letterSpacing='0px'
      font='greycliff'
      weight='600'
      size='16'
      color='blueGreycliff'
      lineHeight='24px'
      display='block'
    >
      There are two keys inside:
    </SrText>
    <SrBox paddingLeft='10px'>
      <SrText
        letterSpacing='0px'
        font='greycliff'
        weight='600'
        size='16'
        color='blueGreycliff'
        lineHeight='24px'
        display='block'
      >
        1. Owner's key to the original lock
      </SrText>
    </SrBox>
    <SrBox paddingLeft='8px'>
      <SrText
        letterSpacing='0px'
        font='greycliff'
        weight='600'
        size='16'
        color='blueGreycliff'
        lineHeight='24px'
        display='block'
      >
        2. Key to the installed Sunlock
      </SrText>
    </SrBox>
  </>
);

SrPartnerListingAccessInstructions.propTypes = {
  listing: PropTypes.object.isRequired,
  marketingPartnerListing: PropTypes.object,
};

SrPartnerListingAccessInstructions.displayName = 'SrPartnerListingAccessInstructions';

export default SrPartnerListingAccessInstructions;
